import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import Raven from 'raven-js';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FREE, STARTER } from 'self-service-api/constants/ProductLevels';
import { AllProductLines, CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { UPGRADE_PAGE } from 'ui-addon-upgrades/_core/common/constants/PurchaseMotionLocations';
import { getCanViewNewSeats } from 'ui-addon-upgrades/_core/common/eligibility/getCanViewNewSeats';
import FeatureTable, { TableContainer } from 'ui-addon-upgrades/featureTable/FeatureTable';
import { FeatureTableBadge } from 'ui-addon-upgrades/featureTable/sharedComponents';
import ProductIcon from '../../components/common/ProductIcon';
import SkeletonLoadWrapper from '../../components/common/loading/SkeletonLoadWrapper';
import SuiteTileCTAsContainer from '../../components/merch-card/SuiteTileCTAsContainer';
import { CrmSuitePrice } from '../../components/merch-card/price-display/CrmSuitePrice';
import { SUITE_STARTER_FEATURE_TABLE_CONTAINER } from '../../constants/ErrorBoundaryNames';
import { SUITE_FEATURE_TABLE_HEADER, UPGRADE_PAGES } from '../../constants/tracking/Locations';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { withErrorBoundary } from '../ErrorBoundary';
import buildGetFeaturesPromiseByProductLine from '../utils/buildGetFeaturesPromisesByProductLine';
import CommerceHubBody from './CommerceHubBody';
import FeatureTableFreePriceDisplay from './FeatureTableFreePriceDisplay';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const LabelContent = ({
  productLine
}) => {
  return /*#__PURE__*/_jsxs("div", {
    className: "display-flex align-center",
    style: {
      marginLeft: '-24px'
    },
    children: [/*#__PURE__*/_jsx(ProductIcon, {
      productLine: productLine,
      width: "24",
      className: "m-right-3"
    }), /*#__PURE__*/_jsx("strong", {
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "suiteFeatureTable.section.title",
        options: {
          product: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `productLine.${productLine}`
          })
        }
      })
    })]
  });
};
const buildPaidSuiteStarterHeaderData = hasPricingAndPurchasingSucceeded => {
  return {
    FREE: {
      Content: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'products.crm.FREE'
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "m-y-1",
          children: /*#__PURE__*/_jsx(FeatureTableFreePriceDisplay, {})
        }), /*#__PURE__*/_jsx("div", {
          "aria-hidden": "true",
          style: {
            height: '40px'
          }
        })]
      })
    },
    STARTER: {
      Content: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FeatureTableBadge, {
          badgeStyle: {
            margin: '0 auto',
            width: 'fit-content',
            marginBottom: '8px'
          },
          badgeType: "recommended"
        }), /*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'products.crm.STARTER'
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "m-y-1",
          children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: !hasPricingAndPurchasingSucceeded,
            heights: [19],
            widths: [190],
            className: "flex-column m-y-2",
            Content: CrmSuitePrice,
            contentProps: {
              productLevel: STARTER,
              concise: true
            }
          })
        }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !hasPricingAndPurchasingSucceeded,
          heights: [40],
          children: /*#__PURE__*/_jsx(SuiteTileCTAsContainer, {
            productLevel: STARTER,
            trackingSource: SUITE_FEATURE_TABLE_HEADER,
            ctaStyles: {
              width: '154px',
              padding: '11px'
            },
            returnFirstCTAOnly: true,
            isFreeToStarter: true,
            location: UPGRADE_PAGE
          })
        })]
      })
    }
  };
};
const buildPaidSuiteStarterBodyData = features => {
  return {
    sharedData: {
      levels: [FREE, STARTER],
      subscreen: UPGRADE_PAGES.SUITE_STARTER
    },
    sectionData: {
      MARKETING: {
        features: features[MARKETING] || [],
        LabelContent: /*#__PURE__*/_jsx(LabelContent, {
          productLine: MARKETING
        })
      },
      SALES: {
        features: features[SALES] || [],
        LabelContent: /*#__PURE__*/_jsx(LabelContent, {
          productLine: SALES
        })
      },
      SERVICE: {
        features: features[SERVICE] || [],
        LabelContent: /*#__PURE__*/_jsx(LabelContent, {
          productLine: SERVICE
        })
      },
      CMS: {
        features: features[CMS] || [],
        LabelContent: /*#__PURE__*/_jsx(LabelContent, {
          productLine: CMS
        })
      },
      OPERATIONS: {
        features: features[OPERATIONS] || [],
        LabelContent: /*#__PURE__*/_jsx(LabelContent, {
          productLine: OPERATIONS
        })
      }
    }
  };
};
const SuiteStarterFeatureTableContainer = () => {
  const [fetchedData, setFetchedData] = useState({
    features: {},
    canViewNewSeats: false,
    loading: true
  });
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  useEffect(() => {
    const allProductLinesGetFeaturesPromises = AllProductLines.map(productLine => {
      return buildGetFeaturesPromiseByProductLine(productLine);
    });
    Promise.all([...allProductLinesGetFeaturesPromises, getCanViewNewSeats()]).then(response => {
      const featureData = {};
      AllProductLines.forEach((productLine, i) => {
        featureData[productLine] = response[i];
      });
      setFetchedData({
        features: featureData,
        canViewNewSeats: !!response[AllProductLines.length],
        loading: false
      });
    }).catch(error => {
      Raven.captureException(new Error('Failed to fetch SuiteStarterFeatureTableContainer data'), {
        extra: {
          message: error.message
        }
      });
    });
  }, []);
  const headerData = buildPaidSuiteStarterHeaderData(hasPricingAndPurchasingSucceeded);
  const bodyData = buildPaidSuiteStarterBodyData(fetchedData.features);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FeatureTable, {
      headerData: headerData,
      bodyData: bodyData,
      canViewNewSeats: fetchedData.canViewNewSeats
    }), /*#__PURE__*/_jsx(TableContainer, {
      numColumns: 2,
      children: /*#__PURE__*/_jsx(CommerceHubBody, {})
    })]
  });
};
export default withErrorBoundary(SuiteStarterFeatureTableContainer, {
  boundaryName: SUITE_STARTER_FEATURE_TABLE_CONTAINER
});